import type { PropsWithChildren } from 'react';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'next-i18next';
import dynamic from 'next/dynamic';

import Box from '@mui/material/Box';

import Typography from 'src/components/ui/Typography';
import { MapTypeId } from 'src/components/map/GoogleMap';
import IconButton from 'src/components/ui/IconButton';

import { styledComponent } from 'src/utils/styled';

import { colours } from 'src/constants/colours';
import useTimeout from 'src/hooks/useTimeout';

const MiniMapIconOverlay = dynamic(() => import('src/assets/icons/mini-map-icon-overlay.svg'), {
    ssr: false,
});

const MiniMapTypeTerrain = dynamic(() => import('src/assets/icons/mini-map-type-terrain.svg'), {
    ssr: false,
});
const MiniMapTypeSatellite = dynamic(() => import('src/assets/icons/mini-map-type-satellite.svg'), {
    ssr: false,
});
const MiniMapTypeRoadmap = dynamic(() => import('src/assets/icons/mini-map-type-roadmap.svg'), {
    ssr: false,
});
const MiniMapTypeTraffic = dynamic(() => import('src/assets/icons/mini-map-type-traffic.svg'), {
    ssr: false,
});
const MiniMapTypeBicycling = dynamic(() => import('src/assets/icons/mini-map-type-bicycling.svg'), {
    ssr: false,
});
const MiniMapTypeTransit = dynamic(() => import('src/assets/icons/mini-map-type-transit.svg'), {
    ssr: false,
});

const MiniMapIconOverlayWrapper = styledComponent(Box)`
    width: 3rem;
    height: 3rem;
    border-radius: 6.25rem;
    box-shadow: 0 0.25rem 0.5rem 0 ${colours.blackOpacity.O16};
    cursor: pointer;
`;

const MapAndTextTypeWrapper = styledComponent(Box)`
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 4.75rem;
    > div:first-of-type {
        margin-bottom: 0.5rem;
    }
`;

const MiniMapTypeWrapper = styledComponent(Box)<{ $isSelected: boolean }>`
    display: flex;
    align-items: center;
    width: 3rem;
    height: 3rem;
    border-radius: 1.8125rem;
    cursor: pointer;
    ${({ $isSelected }) => $isSelected && `border: 0.125rem solid ${colours.secondary.main};`};
`;

const MiniMapsSelectionContainer = styledComponent(Box)`
    background-color: ${colours.white.main};
    display: flex;
    flex-direction: column;
    padding: 0.75rem 1rem;
    border-radius: 1rem;
    box-shadow: 0 0.25rem 0.5rem 0 ${colours.blackOpacity.O16};
`;

const MiniMapsRowSelectionContainer = styledComponent(Box)`
    display: flex;
    flex-direction: row;
    > div {
        margin-right: 0.75rem;
    }
    > div:last-of-type {
        margin-right: 0rem;
    }
`;

const Label: React.FC<PropsWithChildren<{ isSelected: boolean }>> = ({ children, isSelected }) => (
    <Typography
        textAlign="center"
        fontSize="0.75rem"
        fontWeight={isSelected ? 700 : 400}
        color={isSelected ? colours.secondary.main : colours.primary.main}
    >
        {children}
    </Typography>
);

type Props = {
    setMapTypeId: React.Dispatch<React.SetStateAction<MapTypeId>>;
    mapTypeId: MapTypeId;
    setAddTrafficLayer: React.Dispatch<React.SetStateAction<boolean>>;
    addTrafficLayer: boolean;
    setAddBicyclingLayer: React.Dispatch<React.SetStateAction<boolean>>;
    addBicyclingLayer: boolean;
    setAddTransitLayer: React.Dispatch<React.SetStateAction<boolean>>;
    addTransitLayer: boolean;
};

const GoogleMapTypeControl: React.FC<Props> = ({
    setMapTypeId,
    mapTypeId,
    setAddTrafficLayer,
    addTrafficLayer,
    setAddBicyclingLayer,
    addBicyclingLayer,
    setAddTransitLayer,
    addTransitLayer,
}) => {
    const { t } = useTranslation(['ad']);
    const [isTypeControlHovered, setIsTypeControlHovered] = useState(false);
    const [selectionBoxIsVisible, setSelectionBoxIsVisible] = useState(false);
    const [openedByClic, setOpenedByClic] = useState(false); // used when we use clics (close button mainly)

    const optionsMapType = [
        {
            label: t('ad:mapTypeSimple'),
            isSelected: mapTypeId === MapTypeId.ROADMAP,
            action: () => setMapTypeId(MapTypeId.ROADMAP),
            Image: MiniMapTypeRoadmap,
        },
        {
            label: t('ad:mapTypeSatellite'),
            isSelected: mapTypeId === MapTypeId.HYBRID,
            action: () => setMapTypeId(MapTypeId.HYBRID),
            Image: MiniMapTypeSatellite,
        },
        {
            label: t('ad:mapTypeTerrain'),
            isSelected: mapTypeId === MapTypeId.TERRAIN,
            action: () => setMapTypeId(MapTypeId.TERRAIN),
            Image: MiniMapTypeTerrain,
        },
    ];
    const optionsExtra = [
        {
            label: t('ad:mapTypeTrafic'),
            isSelected: addTrafficLayer,
            action: () => setAddTrafficLayer((prevValue) => !prevValue),
            Image: MiniMapTypeTraffic,
        },
        {
            label: t('ad:mapTypeTransit'),
            isSelected: addTransitLayer,
            action: () => setAddTransitLayer((prevValue) => !prevValue),
            Image: MiniMapTypeTransit,
        },
        {
            label: t('ad:mapTypeBicycling'),
            isSelected: addBicyclingLayer,
            action: () => setAddBicyclingLayer((prevValue) => !prevValue),
            Image: MiniMapTypeBicycling,
        },
    ];
    // if the selection container is visible but we are not hovering over it with our cursor, await 1s before close
    useTimeout(() => setSelectionBoxIsVisible(false), selectionBoxIsVisible && !isTypeControlHovered ? 1000 : null);

    useEffect(() => {
        setOpenedByClic(selectionBoxIsVisible);
    }, [selectionBoxIsVisible]);

    useEffect(() => {
        setSelectionBoxIsVisible(openedByClic);
    }, [openedByClic]);

    return (
        <Box
            display="flex"
            flexDirection="row"
            margin="0.5rem"
            onMouseEnter={() => {
                setIsTypeControlHovered(true);
                setSelectionBoxIsVisible(true);
            }}
            onClick={() => {
                setIsTypeControlHovered(true);
                setSelectionBoxIsVisible(true);
            }}
            onMouseLeave={() => setIsTypeControlHovered(false)}
        >
            {!selectionBoxIsVisible && (
                <MiniMapIconOverlayWrapper>
                    <MiniMapIconOverlay height="3rem" width="3rem" />
                </MiniMapIconOverlayWrapper>
            )}
            {selectionBoxIsVisible && openedByClic && (
                <MiniMapsSelectionContainer>
                    <Box
                        marginBottom="1rem"
                        display="flex"
                        flexDirection="row"
                        justifyContent="space-between"
                        alignItems="center"
                    >
                        <Typography color={colours.black.main} variant="h5">
                            {t('ad:mapType')}
                        </Typography>
                        <IconButton
                            onClick={() => setOpenedByClic(false)}
                            color="white"
                            iconProps={{
                                name: 'Close',
                                size: '1rem',
                            }}
                        />
                    </Box>
                    <MiniMapsRowSelectionContainer>
                        {optionsMapType.map(({ label, isSelected, action, Image }) => (
                            <MapAndTextTypeWrapper key={label}>
                                <MiniMapTypeWrapper $isSelected={isSelected} onClick={action}>
                                    <Image height="3rem" width="3rem" />
                                </MiniMapTypeWrapper>
                                <Label isSelected={isSelected}>{label}</Label>
                            </MapAndTextTypeWrapper>
                        ))}
                    </MiniMapsRowSelectionContainer>
                    <Box margin="1rem 0">
                        <Typography color={colours.black.main} variant="h5">
                            {t('ad:mapTypeMoreInformation')}
                        </Typography>
                    </Box>
                    <MiniMapsRowSelectionContainer>
                        {optionsExtra.map(({ label, isSelected, action, Image }) => (
                            <MapAndTextTypeWrapper key={label}>
                                <MiniMapTypeWrapper $isSelected={isSelected} onClick={action}>
                                    <Image height="3rem" width="3rem" />
                                </MiniMapTypeWrapper>
                                <Label isSelected={isSelected}>{label}</Label>
                            </MapAndTextTypeWrapper>
                        ))}
                    </MiniMapsRowSelectionContainer>
                </MiniMapsSelectionContainer>
            )}
        </Box>
    );
};

export default GoogleMapTypeControl;
